import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Dropdown, Tag, Switch, message, Input, InputNumber, Upload, Spin, Select } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, PoweroffOutlined, MoreOutlined, UploadOutlined } from "@ant-design/icons";

import {
  getBannersApi,
  createBannerApi,
  updateBannerApi,
  deleteBannerApi,
  uploadImageApi,
  deleteImageApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";
import AppInput from "../../common/FormInputs/AppInput";

const ACCENT = "#534ab7";

export default function Banners() {
  const [banners,  setBanners]  = useState([]);
  const [open,      setOpen]      = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [imageUrl,  setImageUrl]  = useState("");
  const [uploading, setUploading] = useState(false);
  const [form] = Form.useForm();

  const fetchBanners = async () => {
    try {
      const res = await getBannersApi();
      setBanners((res.data.data || []).slice().sort((a, b) => b.id - a.id));
    } catch {
      message.error("Failed to load banners");
    }
  };

  useEffect(() => { fetchBanners(); }, []);

  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    setImageUrl("");
    form.setFieldsValue({ active: true, sortOrder: 0, showTitleOverlay: true, showShopNowButton: true, bannerType: "CAROUSEL" });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    setImageUrl(record.imageUrl || "");
    form.setFieldsValue({
      title:       record.title,
      subtitle:    record.subtitle,
      redirectUrl: record.redirectUrl,
      badgeText:   record.badgeText,
      sortOrder:   record.sortOrder,
      active:      record.active,
      showTitleOverlay:  record.showTitleOverlay ?? true,
      showShopNowButton: record.showShopNowButton ?? true,
      bannerType: record.bannerType || "CAROUSEL",
    });
    setOpen(true);
  };

  const handleImageUpload = (file) => {
    (async () => {
      setUploading(true);
      try {
        const res = await uploadImageApi(file, "banners");
        const url = res?.data?.data;
        if (url) setImageUrl(url);
      } catch {
        message.error("Image upload failed");
      } finally {
        setUploading(false);
      }
    })();
    return false;
  };

  const handleRemoveImage = async () => {
    if (imageUrl) {
      try { await deleteImageApi(imageUrl); } catch { /* ignore */ }
    }
    setImageUrl("");
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    if (!imageUrl) { message.error("Please upload a banner image"); return; }
    setLoading(true);
    try {
      const payload = { ...values, imageUrl };
      if (editing) {
        await updateBannerApi(editing.id, payload);
        message.success("Banner updated");
      } else {
        await createBannerApi(payload);
        message.success("Banner added");
      }
      setOpen(false);
      fetchBanners();
    } catch (err) {
      message.error(err?.response?.data?.data || "Failed to save banner");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record) => {
    try {
      await updateBannerApi(record.id, { ...record, active: !record.active });
      message.success(record.active ? "Banner deactivated" : "Banner activated");
      fetchBanners();
    } catch {
      message.error("Failed to update status");
    }
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete this banner?",
      content: "It will be removed from the mobile home slider.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteBannerApi(id);
          message.success("Banner deleted");
          fetchBanners();
        } catch (err) {
          message.error(err?.response?.data?.data || "Failed to delete banner");
        }
      },
    });
  };

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      width: 60,
      render: (v) => <span style={{ color: "#aaa", fontSize: 12 }}>#{v}</span>,
    },
    {
      title: "Image",
      dataIndex: "imageUrl",
      width: 120,
      render: (v) =>
        v ? (
          <img src={v} alt="" style={{ width: 90, height: 50, objectFit: "cover", borderRadius: 8, border: "1px solid #eee" }} />
        ) : (
          <div style={{ width: 90, height: 50, borderRadius: 8, background: "#f5f5f5" }} />
        ),
    },
    {
      title: "Title",
      dataIndex: "title",
      render: (v, r) => (
        <div>
          <div style={{ fontWeight: 600, color: "#111827" }}>{v}</div>
          <div style={{ fontSize: 11, color: "#9CA3AF" }}>{r.subtitle}</div>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "bannerType",
      width: 90,
      align: "center",
      render: (v) => {
        const label = v === "STATIC" ? "Static" : v === "ADS" ? "Ads Popup" : "Carousel";
        const color = v === "STATIC" ? "purple" : v === "ADS" ? "volcano" : "blue";
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Sort",
      dataIndex: "sortOrder",
      width: 70,
      align: "center",
      render: (v) => <span style={{ color: "#6B7280", fontSize: 13 }}>{v}</span>,
    },
    {
      title: "Status",
      dataIndex: "active",
      width: 90,
      align: "center",
      render: (v) => <Tag color={v ? "success" : "default"}>{v ? "Active" : "Off"}</Tag>,
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEdit(r) },
              {
                key: "toggle",
                label: r.active ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleActive(r),
              },
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDelete(r.id) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Banners</div>
          <div className="page-sub">{banners.length} banners — Carousel banners show in the home-screen slider, Static shows below the category grid, Ads Popup shows full-screen once when the app opens</div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ background: ACCENT, border: "none", borderRadius: 8 }}
          onClick={openAdd}
        >
          Add Banner
        </Button>
      </div>

      <AppTable columns={columns} data={banners} />

      <Modal
        title={editing ? `Edit — ${editing.title}` : "Add Banner"}
        open={open}
        width={520}
        okText={editing ? "Save Changes" : "Add Banner"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading }}
        onOk={handleSubmit}
        onCancel={() => { setOpen(false); setEditing(null); form.resetFields(); setImageUrl(""); }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Banner Image">
            {imageUrl ? (
              <div style={{ position: "relative", display: "inline-block" }}>
                <img src={imageUrl} alt="" style={{ width: 220, height: 110, objectFit: "cover", borderRadius: 10, border: "1px solid #eee" }} />
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  style={{ position: "absolute", top: 4, right: 4, padding: "0 6px", minWidth: 0 }}
                  onClick={handleRemoveImage}
                />
              </div>
            ) : (
              <Upload accept="image/*" beforeUpload={handleImageUpload} showUploadList={false} disabled={uploading}>
                <Button icon={<UploadOutlined />} loading={uploading}>Upload Image</Button>
              </Upload>
            )}
            {uploading && (
              <div style={{ marginTop: 8 }}><Spin size="small" /> <span style={{ fontSize: 12, color: "#888" }}>Uploading…</span></div>
            )}
          </Form.Item>

          <Form.Item
            name="bannerType"
            label="Banner Type"
            tooltip="Carousel = shown as a slide in the home-screen slider. Static = a single fixed image shown below the category grid instead. Ads Popup = a full-screen ad shown once over the home screen when the app opens, until the user closes it."
          >
            <Select>
              <Select.Option value="CAROUSEL">Carousel (slider)</Select.Option>
              <Select.Option value="STATIC">Static (below categories)</Select.Option>
              <Select.Option value="ADS">Ads Popup (full-screen, on app load)</Select.Option>
            </Select>
          </Form.Item>

          <AppInput
            name="title"
            label="Title"
            rules={[{ required: true, message: "Title is required" }]}
            placeholder="e.g. Flash Sale — Up to 50% Off"
          />

          <AppInput name="subtitle" label="Subtitle" placeholder="e.g. On all baby clothing this weekend" />

          <div style={{ display: "flex", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <AppInput name="redirectUrl" label="Redirect URL / Deep Link (optional)" placeholder="e.g. /offers" />
            </div>
            <div style={{ flex: 1 }}>
              <AppInput name="badgeText" label="Badge Emoji/Text (optional)" placeholder="e.g. 🔥" />
            </div>
          </div>

          <div style={{ display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
            <Form.Item name="sortOrder" label="Sort Order" style={{ width: 120 }}>
              <InputNumber min={0} max={999} style={{ width: "100%", borderRadius: 8 }} />
            </Form.Item>
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item
              name="showTitleOverlay"
              label="Show Title/Subtitle on Image"
              valuePropName="checked"
              tooltip="Turn off if the uploaded image already has its own text baked in"
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
            <Form.Item
              name="showShopNowButton"
              label="Show 'Shop Now' Button"
              valuePropName="checked"
              tooltip="Turn off if the image already has its own call-to-action"
            >
              <Switch checkedChildren="Yes" unCheckedChildren="No" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
